import CryptoJS from 'crypto-js';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

function b64UrlEncode(bytes) {
  const wordArray = CryptoJS.lib.WordArray.create(bytes);
  const b64 = CryptoJS.enc.Base64.stringify(wordArray);
  return b64.replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/g,'');
}

function b64UrlDecode(str) {
  str = str.replace(/-/g,'+').replace(/_/g,'/');
  const pad = str.length % 4;
  if (pad) str += '='.repeat(4-pad);
  const words = CryptoJS.enc.Base64.parse(str);
  const bytes = [];
  for (let i = 0; i < words.sigBytes; i++) {
    bytes.push((words.words[Math.floor(i/4)] >> (24 - 8*(i%4))) & 0xff);
  }
  return Uint8Array.from(bytes);
}

function u64be(number) {
  const b = new Uint8Array(8);
  let v = Math.floor(number);
  for (let i = 7; i >= 0; i--) { b[i] = v & 0xff; v = v >>> 8; }
  return b;
}

function concatUint8(...arrays) {
  const total = arrays.reduce((s,a)=> s+a.length,0);
  const out = new Uint8Array(total);
  let offset=0;
  for(const a of arrays){ out.set(a,offset); offset+=a.length; }
  return out;
}

// 导入 Fernet key
function importKeySync(keyBase64) {
  const keyBytes = b64UrlDecode(keyBase64);
  return {
    signingKey: keyBytes.slice(0,16),
    encryptionKey: keyBytes.slice(16)
  };
}

// HMAC-SHA256
function hmacSha256Sync(signingKey, dataBytes) {
  const wordArray = CryptoJS.lib.WordArray.create(dataBytes);
  const keyWordArray = CryptoJS.lib.WordArray.create(signingKey);
  const hmac = CryptoJS.HmacSHA256(wordArray, keyWordArray);
  const result = [];
  for(let i=0;i<hmac.sigBytes;i++){
    result.push((hmac.words[Math.floor(i/4)] >> (24 - 8*(i%4))) & 0xff);
  }
  return new Uint8Array(result);
}

// AES-CBC
function aesCbcEncryptSync(encryptionKey, iv, plainBytes) {
  const keyWord = CryptoJS.lib.WordArray.create(encryptionKey);
  const ivWord = CryptoJS.lib.WordArray.create(iv);
  const plainWord = CryptoJS.lib.WordArray.create(plainBytes);
  const encrypted = CryptoJS.AES.encrypt(plainWord, keyWord, { iv: ivWord, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 });
  return b64UrlDecode(encrypted.ciphertext.toString(CryptoJS.enc.Base64));
}

function aesCbcDecryptSync(encryptionKey, iv, cipherBytes) {
  const keyWord = CryptoJS.lib.WordArray.create(encryptionKey);
  const ivWord = CryptoJS.lib.WordArray.create(iv);
  const ctWord = CryptoJS.lib.WordArray.create(cipherBytes);
  const decrypted = CryptoJS.AES.decrypt({ ciphertext: ctWord }, keyWord, { iv: ivWord, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 });
  return new Uint8Array(decrypted.words.flatMap(w => [(w>>24)&0xff,(w>>16)&0xff,(w>>8)&0xff,w&0xff]).slice(0, decrypted.sigBytes));
}

// 生成 Fernet key
export function generateKeySync() {
  const key = new Uint8Array(32);
  crypto.getRandomValues(key);
  return b64UrlEncode(key);
}

// 加密
export function encryptFernetSync(plaintext, keyBase64) {
  const { signingKey, encryptionKey } = importKeySync(keyBase64);
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const now = Math.floor(Date.now()/1000);
  const version = new Uint8Array([0x80]);
  const timestamp = u64be(now);
  const plainBytes = textEncoder.encode(plaintext);
  const ciphertext = aesCbcEncryptSync(encryptionKey, iv, plainBytes);
  const body = concatUint8(version, timestamp, iv, ciphertext);
  const hmac = hmacSha256Sync(signingKey, body);
  const token = concatUint8(body, hmac);
  return b64UrlEncode(token);
}

// 解密
export function decryptFernetSync(tokenBase64, keyBase64, ttlSeconds=null) {
  const tokenBytes = b64UrlDecode(tokenBase64);
  const version = tokenBytes[0];
  if(version!==0x80) throw new Error('不支持的 Fernet 版本');
  const timestampBytes = tokenBytes.slice(1,9);
  const iv = tokenBytes.slice(9,25);
  const hmacBytes = tokenBytes.slice(tokenBytes.length-32);
  const ciphertext = tokenBytes.slice(25, tokenBytes.length-32);
  const { signingKey, encryptionKey } = importKeySync(keyBase64);
  const body = tokenBytes.slice(0, tokenBytes.length-32);
  const sig = hmacSha256Sync(signingKey, body);
  if(!sig.every((v,i)=>v===hmacBytes[i])) throw new Error('HMAC 校验失败');
  const ts = timestampBytes.reduce((acc,b,i)=> acc + (b << (8*(7-i))),0);
  if(ttlSeconds!=null && Math.floor(Date.now()/1000)-ts>ttlSeconds) throw new Error('token 已过期');
  const ptBytes = aesCbcDecryptSync(encryptionKey, iv, ciphertext);
  return { plaintext: textDecoder.decode(ptBytes), iat: ts };
}
